import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import { ActionIcon, Tooltip } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Organization, Patient, Reference } from '@medplum/fhirtypes';
import { IconPhone } from '@tabler/icons-react';
import { ReactElement, useCallback } from 'react';

type CallButtonProps = {
  phoneNumber?: string;
  /**
   * Who the number belongs to, when the caller knows. Passed through to the outbound call so
   * its Communication is filed against this patient or partner rather than whoever the phone
   * lookup happens to find, the same way the timeline headers dial.
   */
  recipient?: Reference<Patient | Organization>;
  size?: number;
  tooltip?: string;
};

export function CallButton({ phoneNumber, recipient, size = 14, tooltip }: CallButtonProps): ReactElement | null {
  const { makeCall } = useTwilioPhone();

  const handleCallNumber = useCallback(
    (phoneNumber: string) => {
      if (phoneNumber) {
        makeCall(phoneNumber, undefined, recipient ? getReferenceString(recipient) : undefined);
      }
    },
    [makeCall, recipient]
  );

  if (!phoneNumber) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCallNumber(phoneNumber);
  };

  const label = tooltip || 'Call patient';

  return (
    <Tooltip label={label}>
      <ActionIcon variant="light" color="blue" aria-label={label} onClick={handleClick}>
        <IconPhone size={size} />
      </ActionIcon>
    </Tooltip>
  );
}
